import { useCallback } from 'react';
import type { AddSuccessorStepsRequest, ProcessDraftStep } from '@bliks-io/contracts';
import type { EditableStepPatch, SuccessorCollectionKey } from '@bliks-io/ui';
import { useUpdateDraftStep, useAddDraftSuccessorSteps } from '@/api/mutations/useProcessDraft';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { SUCCESSOR_MODEL_BY_TYPE } from '@/lib/successorEntityMapping';
import { useProcessDraftCache } from '../actions/useProcessDraftCache';
import { useUndoableEditorActions } from '../actions/useUndoableEditorActions';
import type { TerminalStatus } from '../edge-actions/successorInvariant';
import { expandRuleIdToOutputGroup } from '../graph/expandRuleIdToOutputGroup';
import { resolveNonOverlappingPosition } from '../graph/position';
import { toUpdateDraftStepRequest } from '../adapters/patchAdapters';
import { findRuleSuccessorTarget, findSuccessorTarget } from './resolveSuccessorDisconnect';

export interface DraftManualStepHandlers {
  handlePatch: (patch: EditableStepPatch) => Promise<void>;
  /** Stage/clear this step's survey re-request flag (applied on publish). */
  handleSetResurveyRequested: (value: boolean) => Promise<void>;
  handleAddSuccessorSteps: (body: AddSuccessorStepsRequest) => Promise<void>;
  handleRemoveSuccessorStep: (type: SuccessorCollectionKey, entityId: string) => Promise<void>;
  handleMarkAsTerminal: (status: TerminalStatus) => Promise<void>;
  handleMarkRuleAsTerminal: (ruleId: string, status: TerminalStatus) => Promise<void>;
  handleAddRuleSuccessorSteps: (ruleId: string, body: AddSuccessorStepsRequest) => Promise<void>;
  handleRemoveRuleSuccessorStep: (
    ruleId: string,
    type: SuccessorCollectionKey,
    entityId: string
  ) => Promise<void>;
}

/**
 * Extracts all mutation/handler callbacks for DraftManualStepPanel so the
 * component file stays under the max-lines threshold.
 */
export function useDraftManualStepHandlers(
  step: ProcessDraftStep,
  processId: string
): DraftManualStepHandlers {
  const updateMutation = useUpdateDraftStep(processId);
  const addSuccessorStepsMutation = useAddDraftSuccessorSteps(processId);
  const { onDisconnectSteps, markStepAsTerminal, markRulesAsTerminal } = useUndoableEditorActions({
    processId
  });
  const {
    getCachedSteps,
    getCachedDraftAssignments,
    getCachedStepPositions,
    getCachedGeneratedPositions
  } = useProcessDraftCache(processId);

  const stepStableId = String(step.stableStepId);

  const updateMutationAsync = updateMutation.mutateAsync;
  const handlePatch = useCallback(
    async (patch: EditableStepPatch) => {
      // Debounced field edits flush when the keyed panel unmounts, which can
      // land after this step stopped existing — a temp-id placeholder swapped
      // for the server step, or an undo/refetch removing a focused node (no
      // blur fires). PATCHing the dead id 404s, and the mutation's onError
      // rollback would then discard the user's unrelated optimistic edits.
      if (!getCachedSteps().some((s) => String(s.stableStepId) === stepStableId)) return;
      await updateMutationAsync({
        stepId: step._id,
        patch: toUpdateDraftStepRequest(patch)
      });
    },
    [updateMutationAsync, step._id, getCachedSteps, stepStableId]
  );

  const handleSetResurveyRequested = useCallback(
    async (value: boolean) => {
      // `resurveyRequested` is a first-class UpdateDraftStep field, so it goes
      // straight through — no EditableStepPatch content-adapter needed.
      await updateMutationAsync({
        stepId: step._id,
        patch: { resurveyRequested: value }
      });
    },
    [updateMutationAsync, step._id]
  );

  const handleAddSuccessorSteps = useCallback(
    async (body: AddSuccessorStepsRequest) => {
      // Mirror the drag-to-connect fork position: spawn to the right of the
      // parent, run through the shared overlap resolver so multiple adds
      // don't stack at the same spot. Without this the backend would fall
      // through to `{0, 0}` and the new nodes all land at the canvas origin.
      const parentStable = String(step.stableStepId);
      const positions = getCachedStepPositions();
      const parentPos = positions.get(parentStable);
      let resolvedPosition: { x: number; y: number } | undefined;
      if (parentPos) {
        const occupied = [...positions.values(), ...getCachedGeneratedPositions()];
        resolvedPosition = resolveNonOverlappingPosition(
          { x: parentPos.x + 280, y: parentPos.y },
          occupied
        );
      }
      try {
        await addSuccessorStepsMutation.mutateAsync({
          stepId: step._id,
          body: resolvedPosition ? { ...body, position: resolvedPosition } : body
        });
      } catch (error) {
        toastApiError(error, 'errors.unknown');
      }
    },
    [
      addSuccessorStepsMutation,
      step._id,
      step.stableStepId,
      getCachedStepPositions,
      getCachedGeneratedPositions
    ]
  );

  const handleMarkAsTerminal = useCallback(
    (status: TerminalStatus) => markStepAsTerminal(String(step.stableStepId), status),
    [markStepAsTerminal, step.stableStepId]
  );

  const stepRules = step.rules;

  const handleMarkRuleAsTerminal = useCallback(
    async (ruleId: string, status: TerminalStatus): Promise<void> => {
      // Same-output rules share one handle, so flipping terminal must fan
      // out to every sibling or the handle's end-node marker goes out of sync.
      const expanded = expandRuleIdToOutputGroup(stepRules, ruleId);
      const ruleIds = expanded.length > 0 ? expanded : [ruleId];
      await markRulesAsTerminal(stepStableId, ruleIds, status);
    },
    [markRulesAsTerminal, stepStableId, stepRules]
  );

  const handleRemoveSuccessorStep = useCallback(
    async (type: SuccessorCollectionKey, entityId: string) => {
      const parentStable = String(step.stableStepId);
      // Drop the parent → child connection, leaving the downstream node in
      // place (mirrors the canvas edge "disconnect"). The freshly-added case —
      // where the successor materialized as a draft assignment — is handled by
      // the same lookup; disconnecting it leaves a floating orphan, matching
      // the canvas. The backend recompute on disconnect clears the entity from
      // `task.successors`, so the chip disappears without a metadata PATCH.
      const targetStable = findSuccessorTarget({
        steps: getCachedSteps(),
        assignments: getCachedDraftAssignments(),
        targetModel: SUCCESSOR_MODEL_BY_TYPE[type],
        entityId,
        parentStable
      });
      if (!targetStable) return;
      await onDisconnectSteps({ sourceStepId: parentStable, targetStepId: targetStable });
    },
    [onDisconnectSteps, getCachedSteps, getCachedDraftAssignments, step.stableStepId]
  );

  const handleAddRuleSuccessorSteps = useCallback(
    async (ruleId: string, body: AddSuccessorStepsRequest) => {
      // Mirror the task-level add: spawn to the right of the parent, run
      // through the overlap resolver so multiple adds don't stack.
      const parentStable = String(step.stableStepId);
      const positions = getCachedStepPositions();
      const parentPos = positions.get(parentStable);
      let resolvedPosition: { x: number; y: number } | undefined;
      if (parentPos) {
        const occupied = [...positions.values(), ...getCachedGeneratedPositions()];
        resolvedPosition = resolveNonOverlappingPosition(
          { x: parentPos.x + 280, y: parentPos.y },
          occupied
        );
      }
      try {
        await addSuccessorStepsMutation.mutateAsync({
          stepId: step._id,
          body: {
            ...body,
            ruleId,
            ...(resolvedPosition ? { position: resolvedPosition } : {})
          }
        });
      } catch (error) {
        toastApiError(error, 'errors.unknown');
      }
    },
    [
      addSuccessorStepsMutation,
      step._id,
      step.stableStepId,
      getCachedStepPositions,
      getCachedGeneratedPositions
    ]
  );

  const handleRemoveRuleSuccessorStep = useCallback(
    async (ruleId: string, type: SuccessorCollectionKey, entityId: string) => {
      const parentStable = stepStableId;
      // A legacy child may be tagged with any one of the sibling ids in the
      // output group — match any so the disconnect finds it.
      const expanded = expandRuleIdToOutputGroup(stepRules, ruleId);
      const ruleIds = new Set(expanded.length > 0 ? expanded : [ruleId]);
      // Disconnect the rule's edge to the child rather than deleting the node.
      // Forwarding `ruleId` scopes removal to this output's rule group, so a
      // child consuming several outputs of this parent via one shared edge
      // keeps the others (the backend expands the rule to its output group).
      const targetStable = findRuleSuccessorTarget({
        steps: getCachedSteps(),
        assignments: getCachedDraftAssignments(),
        targetModel: SUCCESSOR_MODEL_BY_TYPE[type],
        entityId,
        parentStable,
        ruleIds
      });
      if (!targetStable) return;
      await onDisconnectSteps({
        sourceStepId: parentStable,
        targetStepId: targetStable,
        ruleId
      });
    },
    [onDisconnectSteps, getCachedSteps, getCachedDraftAssignments, stepStableId, stepRules]
  );

  return {
    handlePatch,
    handleSetResurveyRequested,
    handleAddSuccessorSteps,
    handleRemoveSuccessorStep,
    handleMarkAsTerminal,
    handleMarkRuleAsTerminal,
    handleAddRuleSuccessorSteps,
    handleRemoveRuleSuccessorStep
  };
}
